import { Protein } from '../types';
import { normalizeToken } from './normalize';

export type ProteinAliasMap = Record<string, string | string[]>;

export const DEFAULT_PROTEIN_ALIAS_LOOKUP: Record<string, string> = {
  [Protein.NINGUNA]: Protein.NINGUNA,
  [Protein.POLLO]: Protein.POLLO,
  gallina: Protein.POLLO,
  chicken: Protein.POLLO,
  [Protein.TERNERA]: Protein.TERNERA,
  vacuno: Protein.TERNERA,
  beef: Protein.TERNERA,
  [Protein.CORDERO]: Protein.CORDERO,
  lamb: Protein.CORDERO,
  [Protein.PESCADO]: Protein.PESCADO,
  salmon: Protein.PESCADO,
  sardina: Protein.PESCADO,
  atun: Protein.PESCADO,
  caballa: Protein.PESCADO,
  fish: Protein.PESCADO,
  [Protein.PAVO]: Protein.PAVO,
  turkey: Protein.PAVO,
  [Protein.CONEJO]: Protein.CONEJO,
  rabbit: Protein.CONEJO,
  [Protein.CERDO]: Protein.CERDO,
  pork: Protein.CERDO,
  [Protein.PATO]: Protein.PATO,
  duck: Protein.PATO,
};

/**
 * Build a protein alias lookup from raw config aliases,
 * merging them on top of the default lookup.
 *
 * Accepts either ProteinAliasMap (typed) or unknown (from raw config).
 */
export const buildProteinAliasLookup = (rawAliases: ProteinAliasMap | unknown): Record<string, string> => {
  const lookup = { ...DEFAULT_PROTEIN_ALIAS_LOOKUP };
  if (!rawAliases || typeof rawAliases !== 'object' || Array.isArray(rawAliases)) {
    return lookup;
  }

  Object.entries(rawAliases as Record<string, unknown>).forEach(([rawCanonical, rawAliasesValue]) => {
    const canonical = normalizeToken(rawCanonical);
    if (!canonical) return;

    lookup[canonical] = canonical;

    const aliases = Array.isArray(rawAliasesValue)
      ? rawAliasesValue
      : typeof rawAliasesValue === 'string'
        ? [rawAliasesValue]
        : [];

    aliases.forEach((alias) => {
      const aliasToken = normalizeToken(String(alias));
      if (aliasToken) {
        lookup[aliasToken] = canonical;
      }
    });
  });

  return lookup;
};

/**
 * Build a generic alias lookup from a raw alias map (no defaults).
 * Used for pathology aliases and other non-protein alias maps.
 */
export const buildAliasLookup = (rawAliases: Record<string, string | string[]> | undefined): Record<string, string> => {
  const lookup: Record<string, string> = {};
  if (!rawAliases) {
    return lookup;
  }

  Object.entries(rawAliases).forEach(([rawCanonical, rawAliasValues]) => {
    const canonical = normalizeToken(rawCanonical);
    if (!canonical) return;

    lookup[canonical] = canonical;
    const aliases = Array.isArray(rawAliasValues)
      ? rawAliasValues
      : typeof rawAliasValues === 'string'
        ? [rawAliasValues]
        : [];

    aliases.forEach((rawAlias) => {
      const alias = normalizeToken(String(rawAlias));
      if (alias) {
        lookup[alias] = canonical;
      }
    });
  });

  return lookup;
};

/**
 * Canonicalize a single token through an alias lookup.
 */
export const canonicalizeToken = (value: string, aliasLookup: Record<string, string>): string => {
  const normalized = normalizeToken(value);
  if (!normalized) return '';
  return aliasLookup[normalized] || normalized;
};

/**
 * Protein aliases that map to canonical proteins NO LONGER present in the
 * active cocinada catalog (the catalog only has Pollo, Ternera, Pavo, Cerdo
 * as of April 2026). Legacy veterinary rules and historical configs may still
 * reference them; the admin UI paints these with a "⚠️ proteína no disponible"
 * tag.
 *
 * We do NOT change the canonicalization logic — `canonicalizeToken` still
 * returns the canonical form. This set is purely for display/UX.
 *
 * Includes both the canonical tokens (`cordero`, `conejo`, `pato`, `pescado`)
 * and their accepted aliases (`salmon`, `sardina`, `atun`, `caballa`, `lamb`,
 * `rabbit`, `duck`, `fish`) so the flag works regardless of which form the
 * UI/config stored.
 */
export const DEPRECATED_PROTEIN_ALIASES = new Set<string>([
  // Canonical proteins dropped from cocinada catalog
  'cordero',
  'conejo',
  'pato',
  'pescado',
  // Alias forms referencing those canonicals
  'salmon',
  'sardina',
  'atun',
  'caballa',
  'merluza',
  'lamb',
  'rabbit',
  'duck',
  'fish',
]);

/**
 * Returns true if `raw` refers to a protein that is no longer in the active
 * cocinada catalog.
 */
export const isDeprecatedProteinAlias = (raw: string): boolean => {
  const token = normalizeToken(raw);
  if (!token) return false;
  return DEPRECATED_PROTEIN_ALIASES.has(token);
};
